import React from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import { TEditCustomerDetailsReduxParams } from '~types/feat/customer';

import Modal from '~components/atoms/Modal';
import CustomerDetailsForm from '../CustomerManagementCustomerDetailsForm';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import CustomClipLoader from '~components/atoms/CustomClipLoader';
import CustomerManagementCustomerTransactionTable from '../CustomerManagementCustomerTransactionTable';

import { ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_customerManagementCustomerDetails.module.scss';
import CustomerManagementCustomerCollateralTable from '../CustomerManagementCustomerCollateralTable';
import ConfirmDeleteCollateralModal from '../ConfirmCancelCollateralModal';
import CustomerCollateralDetailsModal from '../CustomerCollateralDetailsModal';
import CustomerManagementTransactionProductDetailsTable from '../CustomerManagementTransactionProductDetailsTable';

const CustomerManagementCustomerDetailsModal = () => {
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(state => state.customer.modals.customerDetailsModalIsOpen);
    const isEditing = useAppSelector(state => state.customer.modals.isEditing);
    const getCustomerDetailsError = useAppSelector(state => state.customer.errors.getCustomerDetails);
    const getCustomerDetailsIsLoading = useAppSelector(state => state.customer.loadings.getCustomerDetails);
    const isLoading = useAppSelector(state => state.customer.loadings.editCustomerDetails);
    const editCustomerDetailsError = useAppSelector(state => state.customer.errors.editCustomerDetails);
    const customerDetails = useAppSelector(state => state.customer.customerDetails);
    const userAccess = useAppSelector(state => state.auth.userDetails.access);
    const allBranches = useAppSelector(state => state.branch.allBranches);
    const collateralDetailsIsOpen = useAppSelector(state => state.customer.modals.collateralDetailsModalIsOpen);
    const isTransactionProductListingModalIsOpen = useAppSelector(state => state.gold.modals.transactionProductListingModalIsOpen);
    const getTransactionListingAttempt = useAppSelector(state => state.gold.loadings.getTransactionListing);

    const hasEditAccess = !!userAccess.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.CustomerManagement);

    const massagedCustomerDetails = {
        ...customerDetails,
        branchCode: allBranches.find(branch => branch.branchId === customerDetails.branchId)?.branchCode || '',
    };

    const setIsEditing = (isEditMode: boolean) => dispatch(combinedActions.customerSetCustomerDetailsModalIsEditing(isEditMode));

    const setTransactionProductListingModalIsOpen = (state: boolean) => dispatch(combinedActions.goldSetTransactionProductListingModalIsOpen(state));

    const handleModalOnClose = () => {
        setIsEditing(false);
        dispatch(combinedActions.customerResetEditCustomerDetailsState());
        dispatch(combinedActions.customerSetCustomerDetailsModalIsOpen(false));
    };

    const handleOnSubmit = (data: TEditCustomerDetailsReduxParams) => isEditing && dispatch(combinedActions.customerEditCustomerDetailsAttempt(data));

    const handleOnCloseDetailsModal = () => dispatch(combinedActions.customerSetCollateralDetailsModalIsOpen(false));

    return (
        <Modal
            title='Customer Details'
            isOpen={isOpen}
            onClose={handleModalOnClose}
        >
            {getCustomerDetailsIsLoading
                ? (
                    <CustomClipLoader
                        blackBorder
                        className={Styles.loader}
                    />
                )
                : (
                    getCustomerDetailsError
                        ? (
                            <Text
                                isError
                                label={getCustomerDetailsError}
                            />
                        )
                        : (
                            <div className={Styles.subcontainer}>
                                {hasEditAccess
                                    && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <ActionButton
                                                label={isEditing ? 'Disable editing' : 'Enable editing'}
                                                onClick={() => setIsEditing(!isEditing)}
                                            />
                                        </div>
                                    )
                                }

                                <CustomerDetailsForm
                                    submitButtonLabel='Update'
                                    actionButtonIsLoading={isLoading}
                                    actionButtonClassName={Styles['form-action-button-disabled']}
                                    actionButtonIsDisabled={!isEditing}
                                    onSubmit={handleOnSubmit}
                                    inputIsDisabled={!isEditing}
                                    error={editCustomerDetailsError}
                                    isEditing={isEditing}
                                    initialCustomerDetails={massagedCustomerDetails}
                                />

                                <ConfirmDeleteCollateralModal />

                                <CustomerManagementCustomerCollateralTable />

                                <CustomerCollateralDetailsModal isOpen={collateralDetailsIsOpen} onClose={handleOnCloseDetailsModal}/>

                                <CustomerManagementCustomerTransactionTable />

                                <CustomerManagementTransactionProductDetailsTable
                                    isOpen={isTransactionProductListingModalIsOpen}
                                    onClose={() => setTransactionProductListingModalIsOpen(false)}
                                    isLoading={getTransactionListingAttempt}
                                />
                            </div>
                        )
                )
            }
        </Modal>
    );
};

export default CustomerManagementCustomerDetailsModal;
